import * as Sentry from "@sentry/node";
import { log } from "../common/logger.js";
import {
  ApiError,
  IncorrectScopeError,
  MaxRetriesError,
  NotFoundError,
  UnauthorizedError,
  UnknownError,
} from "./errors.js";

import packageJson from "../../package.json" with { type: "json" };

import { Credentials } from "../common/credentials.js";

const API_PATH = "/open_api/v2";
const EXPERIMENTAL_API_PATH = "/open_api/experimental";
const USER_AGENT = `zeroheight-mcp-server/${packageJson.version}`;

export enum ApiType {
  OpenApi = "open_api", // /open_api/v2
  OpenApiExperimental = "open_api_experimental", // /open_api/experimental
  Internal = "internal", // /api
}

export enum ResponseStatus {
  Success = "success",
  Error = "error",
  Fail = "fail",
}

/**
 * Open API V2 Response format
 */

interface BaseResponse<T> {
  status: ResponseStatus;
  message: string;
  data: T;
}

interface SuccessResponse<T> extends BaseResponse<T> {
  status: ResponseStatus.Success;
}

interface FailResponse<T> extends BaseResponse<T> {
  status: ResponseStatus.Fail;
}

interface ErrorResponse<T> extends BaseResponse<T> {
  status: ResponseStatus.Error;
}

export type APIResponse<S = {}, F = {}, E = {}> =
  | SuccessResponse<S>
  | FailResponse<F>
  | ErrorResponse<E>;

/**
 * Get the correct URL for production of development depending on the environment variable
 */
export function getZeroheightURL() {
  const zeroheightURL = process.env["ZEROHEIGHT_URL"];
  if (zeroheightURL) {
    return new URL(zeroheightURL);
  }

  if (process.env["NODE_ENV"] === "dev") {
    return new URL("https://zeroheight.dev");
  } else {
    return new URL("https://zeroheight.com");
  }
}

async function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function extractApiErrorMessage(body: string): string | undefined {
  try {
    const parsed = JSON.parse(body) as { message?: unknown; error?: unknown };
    if (typeof parsed.message === "string" && parsed.message.length > 0) {
      return parsed.message;
    }
    if (typeof parsed.error === "string" && parsed.error.length > 0) {
      return parsed.error;
    }
  } catch {
    return undefined;
  }
}

function pathPrefixFor(apiType: ApiType): string {
  switch (apiType) {
    case ApiType.OpenApi:
      return API_PATH;
    case ApiType.OpenApiExperimental:
      return EXPERIMENTAL_API_PATH;
    case ApiType.Internal:
      return "/api";
  }
}

export async function request<R>(
  path: string,
  credentials: Credentials,
  init: RequestInit,
  search?: URLSearchParams,
  apiType: ApiType = ApiType.OpenApi,
  metadata: { clientUserAgent?: string } = {},
): Promise<R> {
  const url = getZeroheightURL();
  url.pathname = pathPrefixFor(apiType) + path;
  if (search) {
    url.search = "?" + search.toString();
  }

  if (process.env["NODE_ENV"] === "dev") {
    process.env["NODE_TLS_REJECT_UNAUTHORIZED"] = "0";
  }

  const maxRetries = 3;
  let retries = 0;

  const headers: Record<string, string> = {
    "X-API-CLIENT-NAME": "mcp-server",
    "X-API-CLIENT-VERSION": packageJson.version,
    "User-Agent": USER_AGENT,
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  if (metadata.clientUserAgent) {
    headers["X-MCP-Client-User-Agent"] = metadata.clientUserAgent;
  }

  if (credentials.type === "jwt") {
    headers["Authorization"] = `Bearer ${credentials.jwt}`;
    headers["X-API-CLIENT-NAME"] = "remote-mcp-server";
    if (credentials.mcpAuthId) {
      headers["X-MCP-Auth-ID"] = String(credentials.mcpAuthId);
    }
  } else if (credentials.type === "oauth_jwt") {
    headers["Authorization"] = `Bearer ${credentials.jwt}`;
    headers["X-API-CLIENT-NAME"] = `remote-mcp-server:${credentials.clientId}`;
  } else {
    headers["X-API-CLIENT"] = credentials.client;
    headers["X-API-KEY"] = credentials.token;
  }

  while (retries < maxRetries) {
    const response = await fetch(url, {
      ...init,
      headers,
    });

    if (response.status === 401) {
      log.error("Unauthorized response from API", {
        response: { status: response.status, body: await response.text() },
      });
      throw new UnauthorizedError();
    } else if (response.status === 429) {
      retries++;
      let waitTime: number;
      if (apiType === ApiType.OpenApi) {
        const responseData = await response.json();
        waitTime = responseData.data.reset_time * 1000 - Date.now();
      } else {
        waitTime = 2 ** retries * 1000;
      }
      await sleep(waitTime);
      continue;
    } else if (response.status < 200 || response.status >= 300) {
      const body = await response.text();
      log.error("API request failed", {
        response: { status: response.status, body },
      });

      if (response.status === 403) {
        throw new IncorrectScopeError();
      } else if (response.status === 404) {
        throw new NotFoundError();
      } else {
        const message = extractApiErrorMessage(body);
        if (response.status === 400 && message) {
          throw new ApiError(message);
        }
        Sentry.captureMessage(
          message
            ? `Unknown error occurred making request to: ${url}: ${message}`
            : `Unknown error occurred making request to: ${url}`,
        );
        throw new UnknownError();
      }
    }

    const responseJson = await response.json();
    return responseJson;
  }

  throw new MaxRetriesError();
}

export async function internalRequest<R>(
  path: string,
  method: "GET" | "POST" | "PUT" | "DELETE",
  body: any,
): Promise<R> {
  const url = getZeroheightURL();
  url.pathname = "/api" + path;

  if (process.env["NODE_ENV"] === "dev") {
    process.env["NODE_TLS_REJECT_UNAUTHORIZED"] = "0";
  }

  const response = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "User-Agent": USER_AGENT,
      "X-API-KEY": process.env["INTERNAL_API_KEY"] || "",
    },
    body: JSON.stringify(body),
  });

  if (response.status < 200 || response.status >= 300) {
    log.error("Internal API request failed", {
      response: { status: response.status, body: await response.text() },
    });

    if (response.status === 401) {
      throw new UnauthorizedError();
    } else if (response.status === 404) {
      throw new NotFoundError();
    } else {
      Sentry.captureMessage(
        `Unknown error occurred making internal request to: ${url}`,
      );
      throw new UnknownError();
    }
  }

  const responseJson = await response.json();
  return responseJson;
}
